import type { Formula } from "building-types/options/formula";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { DocOptionLock, DocOptionSet } from "building-types/options/optionSet";
import type { OptionSetOverrides } from "building-types/options/optionTemplateTypes";
import type { Table } from "common/data/table";
import { castOptionChoicesToExpressionValue, formulaGetIdentifiers, parseFormula } from "../../formula/formulaParsing";
import { getOptionLocksByOptionSetId } from "../optionLocksTools";

export interface LockedOptionSet {
  readonly optionSetId: string;
  readonly relatedOptionSetIds: string[];
}

export interface LockedOption {
  readonly optionSetId: string;
  readonly optionId: string;
  readonly relatedOptionSetIds: string[];
}

export interface OptionLockEvaluation {
  readonly lockedOptionSets: LockedOptionSet[];
  readonly lockedOptions: LockedOption[];
}

/**
 * Evaluates a disconnect condition formula.
 * Disconnect conditions are wrapped with not(), so they lock when they evaluate to false.
 */
function evaluateDisconnectCondition(formula: Formula | null | undefined, optionChoices: OptionChoices): boolean {
  if (!formula) {
    return false;
  }
  try {
    return parseFormula(formula.formula).evaluate(castOptionChoicesToExpressionValue(optionChoices)) === false;
  } catch {
    return false;
  }
}

/**
 * Evaluates option set locks (disconnectCondition) and option-level locks (disconnectConditionOptions)
 */
export function evaluateOptionLocks(
  optionSets: Table<DocOptionSet>,
  optionLocks: Table<DocOptionLock>,
  optionChoices: OptionChoices,
  optionTemplateOverrides?: OptionSetOverrides,
): OptionLockEvaluation {
  const lockedOptionSets: LockedOptionSet[] = [];
  const lockedOptions: LockedOption[] = [];

  const optionLocksByOptionSetId = getOptionLocksByOptionSetId(optionLocks);

  for (const optionSetId of Object.keys(optionSets)) {
    const optionLock = optionLocksByOptionSetId[optionSetId];
    // Option template overrides take precedence over base option locks
    const disconnectCondition =
      optionTemplateOverrides?.[optionSetId]?.disconnectCondition ?? optionLock?.disconnectCondition;

    if (evaluateDisconnectCondition(disconnectCondition, optionChoices)) {
      lockedOptionSets.push({
        optionSetId,
        relatedOptionSetIds: formulaGetIdentifiers(disconnectCondition),
      });
    }

    // Merge base and override conditions - override takes precedence for individual options
    const disconnectConditionOptions = {
      ...(optionLock?.disconnectConditionOptions ?? {}),
      ...(optionTemplateOverrides?.[optionSetId]?.disconnectConditionOptions ?? {}),
    };

    for (const [optionId, formula] of Object.entries(disconnectConditionOptions)) {
      if (evaluateDisconnectCondition(formula, optionChoices)) {
        lockedOptions.push({
          optionSetId,
          optionId,
          relatedOptionSetIds: formulaGetIdentifiers(formula),
        });
      }
    }
  }

  return { lockedOptionSets, lockedOptions };
}
